import type { Tool } from '../types'
import type { AgentRequest, AgentStreamCallbacks, AgentSessionCallbacks, ToolChatJobContext, ToolChatMessage, ToolChatStreamCallbacks, ImportToolsResult } from './types'
import type { DocArticle } from './admin'

import { getAgentUrl } from './agent-url'
import { authHeaders } from './auth-headers'

// ─── Agent streaming ─────────────────────────────────────────────────────────

export async function streamAgent(
  request: AgentRequest,
  callbacks: AgentStreamCallbacks,
): Promise<void> {
  const response = await fetch(`${await getAgentUrl()}/api/agent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const err = await response.text().catch(() => response.statusText)
    throw new Error(err || 'Agent request failed')
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // Safety timeout: if no data arrives for 3 minutes, assume the stream is dead.
  // CAM toolpath generation serializes under a FreeCAD lock, so 14 operations
  // run sequentially on the CAM service — each takes 5-15s.
  let lastActivity = Date.now()
  const STREAM_TIMEOUT_MS = 180_000

  while (true) {
    const readPromise = reader.read()
    const timeoutPromise = new Promise<{ done: true; value: undefined }>((resolve) => {
      const check = () => {
        if (Date.now() - lastActivity > STREAM_TIMEOUT_MS) {
          console.warn('Agent stream timeout — no data for 60s, closing')
          reader.cancel().catch(() => {})
          resolve({ done: true, value: undefined })
        } else {
          setTimeout(check, 5000)
        }
      }
      setTimeout(check, 5000)
    })
    const { done, value } = await Promise.race([readPromise, timeoutPromise])
    if (done) break
    lastActivity = Date.now()
    buffer += decoder.decode(value, { stream: true })

    const parts = buffer.split('\n\n')
    buffer = parts.pop()!

    for (const part of parts) {
      if (!part.trim()) continue

      let eventType = ''
      let dataStr = ''

      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7)
        else if (line.startsWith('data: ')) dataStr = line.slice(6)
      }

      if (!eventType || !dataStr) continue

      try {
        const data = JSON.parse(dataStr)
        switch (eventType) {
          case 'session':
            callbacks.onSession(data.sessionId)
            break
          case 'text':
            callbacks.onToken(data.token)
            break
          case 'write_gcode':
            callbacks.onWriteGCode(data.code, data.estimatedTime, data.operationId, data.label)
            break
          case 'patch_gcode':
            callbacks.onPatchGCode({
              start_line: data.start_line,
              end_line: data.end_line,
              new_code: data.new_code,
            }, data.operationId)
            break
          case 'validation':
            callbacks.onValidation(data.issues)
            break
          case 'memory':
            callbacks.onMemory(data.content)
            break
          case 'geometry':
            callbacks.onGeometry?.(data.geometry)
            break
          case 'tool_call':
            callbacks.onToolCall(data.tool, data.summary)
            break
          case 'pipeline_plan':
            callbacks.onPipelinePlan?.(data.operations)
            break
          case 'pipeline_step':
            callbacks.onPipelineStep?.(data.operationId, data.label, data.status)
            break
          case 'operator_notes':
            callbacks.onOperatorNotes?.(data.notes)
            break
          case 'pdf_review':
            callbacks.onPdfReview?.(data)
            break
          case 'walkthrough':
            callbacks.onWalkthrough?.(data.steps)
            break
          case 'done':
            callbacks.onDone(data.sessionId)
            break
          case 'error':
            callbacks.onError?.(data.message)
            break
        }
      } catch {
        // skip malformed SSE events
      }
    }
  }

  // Flush remaining buffer (last events may not have trailing \n\n)
  if (buffer.trim()) {
    const parts = buffer.split('\n\n')
    for (const part of parts) {
      if (!part.trim()) continue
      let eventType = ''
      let dataStr = ''
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7)
        else if (line.startsWith('data: ')) dataStr = line.slice(6)
      }
      if (!eventType || !dataStr) continue
      try {
        const data = JSON.parse(dataStr)
        switch (eventType) {
          case 'write_gcode':
            callbacks.onWriteGCode(data.code, data.estimatedTime, data.operationId, data.label)
            break
          case 'done':
            callbacks.onDone(data.sessionId)
            break
          case 'error':
            callbacks.onError?.(data.message)
            break
        }
      } catch {
        // skip malformed
      }
    }
  }
}

// ─── Agent terminal session (SSE) ────────────────────────────────────────────

/** Parse SSE events from a readable stream, calling the handler for each event */
async function consumeSSE(
  response: Response,
  handler: (eventType: string, data: Record<string, unknown>) => void,
): Promise<void> {
  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const parts = buffer.split('\n\n')
    buffer = parts.pop()!

    for (const part of parts) {
      if (!part.trim()) continue
      let eventType = ''
      let dataStr = ''
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7)
        else if (line.startsWith('data: ')) dataStr = line.slice(6)
      }
      if (!eventType || !dataStr) continue
      try {
        handler(eventType, JSON.parse(dataStr))
      } catch { /* skip malformed */ }
    }
  }

  // Flush remaining buffer
  if (buffer.trim()) {
    for (const part of buffer.split('\n\n')) {
      if (!part.trim()) continue
      let eventType = ''
      let dataStr = ''
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7)
        else if (line.startsWith('data: ')) dataStr = line.slice(6)
      }
      if (!eventType || !dataStr) continue
      try {
        handler(eventType, JSON.parse(dataStr))
      } catch { /* skip malformed */ }
    }
  }
}

function dispatchAgentSessionEvent(
  data: Record<string, unknown>,
  eventType: string,
  callbacks: AgentSessionCallbacks,
): void {
  switch (eventType) {
    case 'agent_text':
      callbacks.onText(data.text as string)
      break
    case 'agent_status':
      callbacks.onStatus(data.id as string, data.label as string, data.status as 'running' | 'done' | 'error', data.detail as string | undefined)
      break
    case 'agent_preview':
      callbacks.onPreview(data.geometry as Record<string, unknown>)
      break
    case 'agent_question':
      callbacks.onQuestion(
        data.field as string,
        data.prompt as string,
        data.inputType as 'text' | 'number' | 'select' | 'machine_picker' | 'file_upload' | 'work_origin',
        data.priority as 'required' | 'recommended',
        data.options as Array<{ value: string; label: string }> | undefined,
      )
      break
    case 'agent_summary':
      callbacks.onSummary(
        data.config as Record<string, unknown>,
        data.readyToGenerate as boolean,
        data.stepValidation as { match: boolean; confidence: number; discrepancies?: string[] } | undefined,
      )
      break
    case 'agent_error':
      callbacks.onError(data.message as string)
      break
    case 'agent_done':
      callbacks.onDone(data.sessionId as string)
      break
  }
}

export async function streamAgentSession(
  files: File[],
  machineProfiles: Array<{ id: string; name: string; type: string }>,
  callbacks: AgentSessionCallbacks,
): Promise<void> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }
  formData.append('machineProfiles', JSON.stringify(machineProfiles))

  const response = await fetch(`${await getAgentUrl()}/api/agent-session`, {
    method: 'POST',
    headers: { ...await authHeaders() },
    body: formData,
  })

  if (!response.ok) {
    const err = await response.text().catch(() => response.statusText)
    throw new Error(err || 'Agent session failed')
  }

  await consumeSSE(response, (eventType, data) => {
    dispatchAgentSessionEvent(data, eventType, callbacks)
  })
}

export async function streamAgentFiles(
  sessionId: string,
  files: File[],
  callbacks: AgentSessionCallbacks,
): Promise<void> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }

  const response = await fetch(`${await getAgentUrl()}/api/agent-session/${sessionId}/files`, {
    method: 'POST',
    headers: { ...await authHeaders() },
    body: formData,
  })

  if (!response.ok) {
    const err = await response.text().catch(() => response.statusText)
    throw new Error(err || 'Agent file upload failed')
  }

  await consumeSSE(response, (eventType, data) => {
    dispatchAgentSessionEvent(data, eventType, callbacks)
  })
}

export async function streamAgentAnswer(
  sessionId: string,
  answers: Record<string, unknown>,
  callbacks: AgentSessionCallbacks,
): Promise<void> {
  const response = await fetch(`${await getAgentUrl()}/api/agent-session/${sessionId}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({ answers }),
  })

  if (!response.ok) {
    const err = await response.text().catch(() => response.statusText)
    throw new Error(err || 'Agent answer failed')
  }

  await consumeSSE(response, (eventType, data) => {
    dispatchAgentSessionEvent(data, eventType, callbacks)
  })
}

// ─── Import tools from file ──────────────────────────────────────────────────

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Strip the data URL prefix (e.g., "data:application/octet-stream;base64,")
      const base64 = result.split(',')[1] ?? result
      resolve(base64)
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

export async function importToolsFromFile(file: File): Promise<ImportToolsResult> {
  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  const isTextFormat = ['csv', 'txt', 'tsv'].includes(ext)

  const fileContent = isTextFormat
    ? await readFileAsText(file)
    : await readFileAsBase64(file)

  const response = await fetch(`${await getAgentUrl()}/api/import-tools`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({ fileContent, fileName: file.name }),
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(err.error || 'Failed to import tools')
  }

  return response.json()
}

// ─── Tool chat streaming ─────────────────────────────────────────────────────

export async function streamToolChat(
  messages: ToolChatMessage[],
  currentTools: Tool[],
  jobContext: ToolChatJobContext,
  callbacks: ToolChatStreamCallbacks,
  fingerprint?: string,
): Promise<void> {
  const response = await fetch(`${await getAgentUrl()}/api/tool-chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({
      messages,
      currentTools: currentTools.map(({ id, ...rest }) => rest),
      jobContext,
      fingerprint,
    }),
  })

  if (!response.ok) {
    const err = await response.text().catch(() => response.statusText)
    throw new Error(err || 'Tool chat failed')
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop()!

    for (const line of lines) {
      if (!line.trim()) continue
      try {
        const event = JSON.parse(line)
        switch (event.type) {
          case 'text':
            callbacks.onToken(event.token)
            break
          case 'tools':
            callbacks.onTools(
              (event.tools as Omit<Tool, 'id'>[]).map((t, i) => ({
                ...t,
                id: crypto.randomUUID(),
                number: t.number ?? i + 1,
              })),
            )
            break
          case 'done':
            callbacks.onDone()
            break
          case 'error':
            callbacks.onError?.(event.message)
            break
        }
      } catch {
        // skip malformed JSON lines
      }
    }
  }

  // Process remaining buffer
  if (buffer.trim()) {
    try {
      const event = JSON.parse(buffer)
      switch (event.type) {
        case 'text':
          callbacks.onToken(event.token)
          break
        case 'tools':
          callbacks.onTools(
            (event.tools as Omit<Tool, 'id'>[]).map((t, i) => ({
              ...t,
              id: crypto.randomUUID(),
              number: t.number ?? i + 1,
            })),
          )
          break
        case 'done':
          callbacks.onDone()
          break
        case 'error':
          callbacks.onError?.(event.message)
          break
      }
    } catch {
      // ignore
    }
  }
}

export interface DwgConversionResult {
  entities: Array<Record<string, unknown>>
  annotations: Array<Record<string, unknown>>
  bounding_box: { min_x: number; min_y: number; max_x: number; max_y: number; width: number; height: number } | null
  layers: Array<{ name: string; color: number }>
  geometry_count: number
  annotation_count: number
  type_counts: Record<string, number>
}

export async function convertDwg(file: File): Promise<DwgConversionResult> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${await getAgentUrl()}/api/convert-dwg`, {
    method: 'POST',
    headers: { ...await authHeaders() },
    body: formData,
  })

  if (!response.ok) {
    const err = await response.text().catch(() => response.statusText)
    throw new Error(err || 'DWG conversion failed')
  }

  return response.json()
}

export interface PdfExtraction {
  match: boolean
  confidence: number
  description: string
  discrepancies?: string[]
  tolerances?: string[]
  surfaceFinishes?: string[]
  notes?: string[]
  material?: string
  partDescription?: string
  dimensions?: { partWidth?: number; partHeight?: number; partDepth?: number }
  needsUserInput?: boolean
  userPrompt?: string
}

export interface PdfMetadata {
  tolerances?: string[]
  surfaceFinishes?: string[]
  notes?: string[]
  partDescription?: string
  material?: string
  dimensions?: { partWidth?: number; partHeight?: number; partDepth?: number }
}

export async function extractPdfMetadata(pdfBase64: string): Promise<PdfMetadata> {
  const response = await fetch(`${await getAgentUrl()}/api/extract-pdf-metadata`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({ pdfBase64 }),
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(err.error || 'PDF metadata extraction failed')
  }

  return response.json()
}

export interface GenerateNotesResult {
  notes: string
  geometry: Record<string, unknown>
  pdfExtraction?: PdfExtraction
  correctionRounds?: number
}

export interface GenerateNotesCallbacks {
  onStep: (data: { step: string; status: string; round?: number; message?: string }) => void
  onValidation: (data: { round: number; match: boolean; confidence: number; description?: string; discrepancies?: string[] }) => void
  onGeometry: (data: { geometry: Record<string, unknown>; round: number; final: boolean }) => void
  onResult: (data: GenerateNotesResult) => void
  onError?: (message: string) => void
}

/**
 * Stream generate-notes with SSE callbacks for real-time progress.
 * Falls back to JSON parsing if the server returns application/json (old server).
 */
export async function streamGenerateNotes(
  dwgResult: DwgConversionResult,
  material?: string,
  fingerprint?: string,
  pdfBase64?: string,
  cachedPdfMetadata?: PdfMetadata,
  callbacks?: GenerateNotesCallbacks,
  userContext?: string,
): Promise<GenerateNotesResult> {
  const response = await fetch(`${await getAgentUrl()}/api/generate-notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...await authHeaders() },
    body: JSON.stringify({
      entities: dwgResult.entities,
      annotations: dwgResult.annotations,
      bounding_box: dwgResult.bounding_box,
      type_counts: dwgResult.type_counts,
      material,
      fingerprint,
      pdfBase64,
      cachedPdfMetadata,
      userContext,
    }),
  })

  if (!response.ok) {
    const err = await response.text().catch(() => response.statusText)
    throw new Error(err || 'Notes generation failed')
  }

  // Fallback: if the server returns JSON (old server without streaming), parse directly
  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.includes('application/json')) {
    const data = await response.json()
    const result: GenerateNotesResult = {
      notes: data.notes,
      geometry: data.geometry,
      pdfExtraction: data.pdfExtraction,
      correctionRounds: data.correctionRounds,
    }
    callbacks?.onResult(result)
    return result
  }

  // SSE streaming path
  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: GenerateNotesResult | null = null

  const processEvent = (eventType: string, dataStr: string) => {
    try {
      const data = JSON.parse(dataStr)
      switch (eventType) {
        case 'notes_step':
          callbacks?.onStep(data)
          break
        case 'notes_validation':
          callbacks?.onValidation(data)
          break
        case 'notes_geometry':
          callbacks?.onGeometry(data)
          break
        case 'notes_result':
          result = {
            notes: data.notes,
            geometry: data.geometry,
            pdfExtraction: data.pdfExtraction,
            correctionRounds: data.correctionRounds,
          }
          callbacks?.onResult(result)
          break
        case 'notes_error':
          callbacks?.onError?.(data.message)
          break
      }
    } catch {
      // skip malformed SSE events
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const parts = buffer.split('\n\n')
    buffer = parts.pop()!

    for (const part of parts) {
      if (!part.trim()) continue
      let eventType = ''
      let dataStr = ''
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7)
        else if (line.startsWith('data: ')) dataStr = line.slice(6)
      }
      if (!eventType || !dataStr) continue
      processEvent(eventType, dataStr)
    }
  }

  // Flush remaining buffer
  if (buffer.trim()) {
    const parts = buffer.split('\n\n')
    for (const part of parts) {
      if (!part.trim()) continue
      let eventType = ''
      let dataStr = ''
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7)
        else if (line.startsWith('data: ')) dataStr = line.slice(6)
      }
      if (!eventType || !dataStr) continue
      processEvent(eventType, dataStr)
    }
  }

  if (!result) {
    throw new Error('Stream ended without a notes_result event')
  }

  return result
}

// ─── File analysis (gap analyzer) ───────────────────────────────────────────

export interface AnalyzeFilesResult {
  extraction: {
    geometry: Record<string, unknown> | null
    material: string | null
    materialSource: 'pdf' | 'step' | 'user' | null
    surfaceFinishes: string[]
    tolerances: string[]
    drawingNotes: string[]
    stockSizeHint: { x: number; y: number; z: number } | null
    fileTypes: Array<'step' | 'dwg' | 'dxf' | 'pdf'>
    sources: Array<{ type: string; filename: string }>
  }
  gapReport: {
    geometry: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string; confidence?: number }
    partDepth: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string; confidence?: number }
    stockZ: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string; confidence?: number }
    material: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string; confidence?: number }
    surfaceFinish: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string }
    tolerances: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string }
    stockSize: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string; confidence?: number }
    machineProfile: { status: 'resolved' | 'inferred' | 'missing' }
    toolCrib: { status: 'resolved' | 'inferred' | 'missing' }
    workOffsets: { status: 'resolved' | 'inferred' | 'missing' }
    units: { status: 'resolved' | 'inferred' | 'missing'; value?: unknown; source?: string }
    readyForGeneration: boolean
    questions: Array<{ field: string; prompt: string; priority: 'required' | 'recommended' }>
  }
  errors?: string[]
}

export async function analyzeFiles(files: File[]): Promise<AnalyzeFilesResult> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }

  const response = await fetch(`${await getAgentUrl()}/api/analyze-files`, {
    method: 'POST',
    headers: { ...await authHeaders() },
    body: formData,
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(err.error || 'File analysis failed')
  }

  return response.json()
}

// ─── Public docs ────────────────────────────────────────────────────────────

export async function fetchPublicDocs(): Promise<DocArticle[]> {
  const baseUrl = await getAgentUrl()
  const res = await fetch(`${baseUrl}/api/docs`)
  if (!res.ok) throw new Error('Failed to fetch docs')
  return res.json()
}

